import { Bookmark, Lock } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import { RANKING_FILTER_LABELS, activeRankingFilters, type RankingFilterKey, type RankingFilters } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import styles from './ranking.module.css';

// F23.09–F23.11 (ADR 0049, as in the mockup): how many the keywords and filters leave, each of them as a chip
// to take off, Clear all, and Save as view. Says plainly that none of it touches a score.

/** ADR 0068: one active line filter, as a chip: the line, then its rating in words. */
export interface LineFilterChip {
  readonly id: string;
  readonly name: string;
  readonly rating: number;
  readonly words: string;
}

interface FilterSummaryProps {
  readonly shown: number;
  readonly total: number;
  readonly keywords: readonly string[];
  readonly filters: RankingFilters;
  readonly onRemoveKeyword: (word: string) => void;
  readonly onRemoveFilter: (key: RankingFilterKey, value: string) => void;
  readonly lineFilters?: readonly LineFilterChip[];
  readonly onRemoveLineFilter?: (id: string) => void;
  readonly onClearAll: () => void;
  readonly onSaveView: (event: MouseEvent<HTMLButtonElement>) => void;
  /** ADR 0069: what sits beside Save as view, at the right end of the row (the Excel export). */
  readonly trailing?: ReactNode;
  /** ADR 0050: the Jev order in use, as a chip that takes it off. */
  readonly jevOrder?: { readonly ask: string; readonly onRemove: () => void } | null;
}

export function FilterSummary({ shown, total, keywords, filters, onRemoveKeyword, onRemoveFilter, lineFilters = [], onRemoveLineFilter, onClearAll, onSaveView, trailing, jevOrder = null }: FilterSummaryProps): ReactNode {
  const active = activeRankingFilters(filters);
  const hasAny = keywords.length > 0 || active.length > 0 || lineFilters.length > 0 || jevOrder !== null;
  return (
    <div className={styles.summary}>
      <span className={styles.summaryCount} aria-live="polite">
        Showing <span className={styles.summaryNumber}>{shown.toLocaleString('en-US')}</span> of {total.toLocaleString('en-US')}
      </span>
      {keywords.map((word) => (
        <Chip key={`kw:${word}`} plain onRemove={() => onRemoveKeyword(word)} removeLabel={`Remove the keyword ${word}`}>
          <span className={styles.chipKind}>Keyword</span> {word}
        </Chip>
      ))}
      {active.map((item) => (
        <Chip key={`${item.key}:${item.value}`} plain onRemove={() => onRemoveFilter(item.key, item.value)} removeLabel={`Remove ${item.label}`}>
          <span className={styles.chipKind}>{RANKING_FILTER_LABELS[item.key]}</span> {item.label}
        </Chip>
      ))}
      {lineFilters.map((item) => (
        <Chip key={`line:${item.id}`} plain onRemove={() => onRemoveLineFilter?.(item.id)} removeLabel={`Remove the ${item.name} filter`}>
          <span className={styles.chipKind}>{item.name}</span> {item.rating} · {item.words}
        </Chip>
      ))}
      {jevOrder && (
        <Chip plain onRemove={jevOrder.onRemove} removeLabel="Stop ordering by Jev">
          <span className={styles.chipKind}>Jev order</span> <span className={styles.jevChipText}>{jevOrder.ask}</span>
        </Chip>
      )}
      {hasAny && (
        <Button size="sm" variant="ghost" onClick={onClearAll}>
          Clear all
        </Button>
      )}
      <span className={styles.summarySpacer} />
      <span className={styles.summaryNote}>
        <Lock size={14} aria-hidden />
        {jevOrder ? 'Keywords and filters narrow the list and Jev only re-orders it; scores never change.' : 'Keywords and filters only narrow the list; scores never change.'}
      </span>
      {trailing}
      <Button size="sm" variant="outline" icon={Bookmark} onClick={onSaveView}>
        Save as view
      </Button>
    </div>
  );
}
